import clsx from 'clsx';
import { m, LazyMotion, domAnimation } from 'framer-motion';
import { StaticImage } from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import React from 'react';
import { useInView } from 'react-intersection-observer';

import DEFAULT_EASE from 'constants/default-ease';
import chartIllustration from 'images/chart-illustration.svg';
import customerLevelIllustration from 'images/customer-level-illustration.svg';
import customerProfileIllustration from 'images/customer-profile-illustration.svg';
import dashboardIllustration from 'images/dashboard.svg';
import growIllustration from 'images/grow-illustration.svg';
import infrastructureIllustration from 'images/infrastructure.svg';
import integrationIllustration from 'images/integration-illustration.svg';
import promotionalIllustration from 'images/promotional-illustration.svg';
import sdkIllustration from 'images/sdk-illustration.svg';
import superchargeIllustration from 'images/supercharge-illustration.svg';

import criticalInfrastructureIllustration from './images/critical-infrastructure-illustration.svg';
import optimizationIllustration from './images/optimization-illustration.svg';
import revenueCatPackageIllustration from './images/revenue-cat-package-illustration.svg';

const illustrationWithBoxShadowOnAllSidesClassName =
  'relative max-w-[622px] -mt-2.5 -mx-4 -mb-5 xl:max-w-[500px] xl:-mt-2 xl:-mx-3 xl:-mb-4 lg:max-w-[612px] lg:min-w-[612px] lg:mt-0 lg:-mx-4 lg:-mb-5 md:max-w-[400px] md:min-w-0 md:-mb-3 xs:max-w-[calc(100%+16px)] xs:-mx-2 xs:-mb-2.5';

const illustrations = {
  'infrastructure-illustration': (
    <img
      className={illustrationWithBoxShadowOnAllSidesClassName}
      src={infrastructureIllustration}
      alt=""
      loading="lazy"
      width={502}
      height={470}
    />
  ),
  'chart-illustration': (
    <img
      className={illustrationWithBoxShadowOnAllSidesClassName}
      src={chartIllustration}
      alt=""
      loading="lazy"
      width={620}
      height={352}
    />
  ),
  'grow-illustration': (
    <img
      className={illustrationWithBoxShadowOnAllSidesClassName}
      src={growIllustration}
      alt=""
      loading="lazy"
      width={606}
      height={359}
    />
  ),
  'dashboard-illustration': (
    <img
      className={illustrationWithBoxShadowOnAllSidesClassName}
      src={dashboardIllustration}
      alt=""
      loading="lazy"
      width={562}
      height={408}
    />
  ),
  'supercharge-illustration': (
    <img
      className={illustrationWithBoxShadowOnAllSidesClassName}
      src={superchargeIllustration}
      alt=""
      loading="lazy"
      width={622}
      height={529}
    />
  ),
  'integration-illustration': (
    <img
      className={illustrationWithBoxShadowOnAllSidesClassName}
      src={integrationIllustration}
      alt=""
      loading="lazy"
      width={622}
      height={490}
    />
  ),
  'sdk-illustration': (
    <img
      className={illustrationWithBoxShadowOnAllSidesClassName}
      src={sdkIllustration}
      alt=""
      loading="lazy"
      width={620}
      height={420}
    />
  ),
  'critical-infrastructure-illustration': (
    <img
      className={illustrationWithBoxShadowOnAllSidesClassName}
      src={criticalInfrastructureIllustration}
      alt=""
      loading="lazy"
      width={533}
      height={586}
    />
  ),
  'revenueCat-package-illustration': (
    <img
      className={illustrationWithBoxShadowOnAllSidesClassName}
      src={revenueCatPackageIllustration}
      alt=""
      loading="lazy"
      width={622}
      height={407}
    />
  ),
  optimization: (
    <img
      className="relative max-w-[592px] xl:-mb-4 xl:max-w-[500px] lg:mb-0 lg:min-w-[612px] lg:max-w-[612px] md:min-w-0 md:max-w-[400px] xs:max-w-[calc(100%+16px)]"
      src={optimizationIllustration}
      alt=""
      loading="lazy"
      width={615}
      height={648}
    />
  ),
  'customer-level-illustration': (
    <img
      className={illustrationWithBoxShadowOnAllSidesClassName}
      src={customerLevelIllustration}
      alt=""
      loading="lazy"
      width={622}
      height={470}
    />
  ),
  'customer-profile-illustration': (
    <img
      className={illustrationWithBoxShadowOnAllSidesClassName}
      src={customerProfileIllustration}
      alt=""
      loading="lazy"
      width={622}
      height={289}
    />
  ),
  'promotional-illustration': (
    <img
      className={illustrationWithBoxShadowOnAllSidesClassName}
      src={promotionalIllustration}
      alt=""
      loading="lazy"
      width={622}
      height={392}
    />
  ),
  'subscribers-access-illustration': (
    <StaticImage
      className="relative m-0 -mt-9 max-w-[476px] xl:-mx-3 xl:-mt-2 xl:-mb-4 lg:-mx-4 lg:mt-0 lg:-mb-5 lg:min-w-[476px] lg:max-w-[476px] md:-mb-3 md:min-w-0 md:max-w-[400px] xs:-mx-2 xs:-mb-2.5 xs:max-w-[calc(100%+16px)]"
      src="./images/subscribers-access-illustration.png"
      alt=""
      loading="lazy"
      width={476}
      height={536}
    />
  ),
  'paywall-illustration': (
    <StaticImage
      className="relative m-0 -mt-1 -mr-5 max-w-[612px] xl:-mx-3 xl:-mt-2 xl:-mb-4 xl:max-w-[500px] lg:-mx-4 lg:mt-0 lg:-mb-5 lg:min-w-[612px] lg:max-w-[612px] md:-mb-3 md:min-w-0 md:max-w-[400px] xs:-mx-2 xs:-mb-2.5 xs:max-w-[calc(100%+16px)]"
      src="./images/paywall-illustration.png"
      alt=""
      loading="lazy"
      width={605}
      height={535}
    />
  ),
};

const SectionWithIllustrationAndColor = ({
  title,
  description,
  theme,
  illustrationName,
  illustrationPosition,
  withBackground,
  withBackgroundIllustration,
  withContentAnimation,
}) => {
  const [wrapperRef, isWrapperInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [contentRef, isContentInView] = useInView({ triggerOnce: true, threshold: 0.7 });

  return (
    <LazyMotion features={domAnimation}>
      <section
        className={clsx(
          'safe-paddings relative',
          { 'bg-gray-1 py-40 xl:py-30 lg:py-20 md:py-14': withBackground === true },
          { 'py-40 xl:py-30 lg:py-20 md:py-14': withBackgroundIllustration === true }
        )}
        ref={wrapperRef}
      >
        {withBackgroundIllustration && (
          <div
            className={clsx(
              'absolute bottom-0 flex h-[275px] w-[450px] justify-end lg:hidden',
              illustrationPosition === 'left' ? 'left-0' : 'right-0 scale-y-100 -scale-x-100'
            )}
          >
            <m.div
              className="h-48 w-48 rounded-full bg-primary-1"
              initial={{ scale: 0 }}
              animate={isWrapperInView && { scale: 1 }}
              transition={{ duration: 1, ease: DEFAULT_EASE }}
              aria-hidden
            />
            <svg
              className="absolute bottom-0 left-0 h-[279px] w-[448px]"
              viewBox="0 0 448 279"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
            >
              <m.path
                d="M-299 152.627C898.875-14.487-110.13 199.379 425 648"
                stroke="#f25a5a"
                strokeWidth="2"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={isWrapperInView && { pathLength: 1 }}
                transition={{ duration: 3, ease: DEFAULT_EASE }}
              />
            </svg>
          </div>
        )}
        <div className="container flex items-center justify-between lg:flex-col">
          <div
            className={clsx(
              'max-w-[488px] xl:max-w-[376px] lg:max-w-[584px] md:w-full md:max-w-none',
              theme === 'red' ? 'theme-red' : 'theme-blue'
            )}
            ref={contentRef}
          >
            <m.h2
              className="text-4xl font-bold leading-tight"
              initial={withContentAnimation && { opacity: 0, y: 20 }}
              animate={withContentAnimation && isContentInView && { opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: DEFAULT_EASE }}
              dangerouslySetInnerHTML={{ __html: title }}
            />
            <m.div
              className="color-list with-link-primary-2 mt-4"
              initial={withContentAnimation && { opacity: 0, y: 20 }}
              animate={withContentAnimation && isContentInView && { opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5, ease: DEFAULT_EASE }}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </div>

          <div
            className={clsx(
              'lg:mt-6',
              illustrationPosition === 'left' && 'order-first lg:order-last'
            )}
            aria-hidden
          >
            {illustrations[illustrationName]}
          </div>
        </div>
      </section>
    </LazyMotion>
  );
};

SectionWithIllustrationAndColor.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  theme: PropTypes.string.isRequired,
  illustrationName: PropTypes.string.isRequired,
  illustrationPosition: PropTypes.string.isRequired,
  withBackground: PropTypes.bool.isRequired,
  withBackgroundIllustration: PropTypes.bool.isRequired,
  withContentAnimation: PropTypes.bool,
};

SectionWithIllustrationAndColor.defaultProps = {
  withContentAnimation: false,
};

export default SectionWithIllustrationAndColor;
